import React, { useEffect, useMemo, useRef } from "react";

import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";

import { useAgentVersionFormContext } from "src/agents/AgentVersionPage/AgentVersionFormProvider";
import { AgentVersionConfigurationInput } from "src/agents/types";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { NarrationLevel, ReasoningLevel } from "src/clients/agent-management";

type ExecutionSettingsEditorProps = {
  immutable: boolean;
};

type ExecutionSettingsFormValues = {
  maxSteps: number | null;
  temperature: number | null;
};

const REASONING_LEVEL_LABELS: Record<ReasoningLevel, string> = {
  none: "None",
  low: "Low",
  medium: "Medium",
  high: "High",
};

/**
 * When the current reasoning level is not supported by the new model,
 * find the closest level by preserving relative position within the scale.
 * Rounds down when an exact positional match is not possible.
 *
 * Special case: if the previous model did not support "none" (i.e. reasoning
 * was always on) and the new model does, we exclude "none" from the target
 * scale so the user doesn't accidentally disable reasoning.
 */
export const mapReasoningLevelByRelativePosition = (
  currentLevel: ReasoningLevel,
  prevSupportedLevels: ReasoningLevel[],
  newSupportedLevels: ReasoningLevel[],
): ReasoningLevel => {
  const prevHadNone = prevSupportedLevels.includes(ReasoningLevel.NONE);
  const newHasNone = newSupportedLevels.includes(ReasoningLevel.NONE);
  const nonNoneLevels = newSupportedLevels.filter(
    (l) => l !== ReasoningLevel.NONE,
  );

  // If previous model had reasoning always-on and the new model introduces
  // "none" as an option, map against the non-none levels so we don't
  // accidentally disable reasoning — unless "none" is the only option.
  const targetLevels =
    !prevHadNone && newHasNone && nonNoneLevels.length > 0
      ? nonNoneLevels
      : newSupportedLevels;

  const prevIdx = prevSupportedLevels.indexOf(currentLevel);
  if (
    prevIdx >= 0 &&
    prevSupportedLevels.length > 1 &&
    targetLevels.length > 1
  ) {
    const relativePos = prevIdx / (prevSupportedLevels.length - 1);
    const newIdx = Math.floor(relativePos * (targetLevels.length - 1));
    return targetLevels[newIdx];
  }
  return targetLevels[0];
};

const NARRATION_LEVEL_LABELS: Record<NarrationLevel, string> = {
  none: "None",
  minimal: "Minimal",
  detailed: "Detailed",
};

const NARRATION_LEVEL_OPTIONS = Object.values(NarrationLevel).map((level) => ({
  key: level,
  value: NARRATION_LEVEL_LABELS[level],
}));

const ReasoningLevelSelector: React.FC<{ immutable: boolean }> = ({
  immutable,
}) => {
  const { control, setValue } =
    useFormContext<AgentVersionConfigurationInput>();
  const { rawModels } = useAgentVersionFormContext();

  const selectedModelId = useWatch({ name: "model_config.model", control });
  const currentReasoningLevel = useWatch({
    name: "model_config.reasoning_level",
    control,
  });

  const selectedModel = rawModels.find((m) => m.model_id === selectedModelId);
  const supportedLevels = useMemo(
    () => selectedModel?.supported_reasoning_levels ?? [],
    [selectedModel?.supported_reasoning_levels],
  );
  const supportsReasoning = supportedLevels.length > 0;

  // Track previous model's supported levels for relative position mapping
  const prevSupportedLevelsRef = useRef<ReasoningLevel[]>([]);

  // Default to lowest supported level when unset, or map to the closest
  // level by relative position when the current value is no longer supported
  useEffect(() => {
    if (immutable || !supportsReasoning) {
      return;
    }

    const prevLevels = prevSupportedLevelsRef.current;
    prevSupportedLevelsRef.current = supportedLevels;

    if (currentReasoningLevel == null) {
      setValue("model_config.reasoning_level", supportedLevels[0]);
    } else if (
      !supportedLevels.includes(currentReasoningLevel as ReasoningLevel)
    ) {
      const mapped = mapReasoningLevelByRelativePosition(
        currentReasoningLevel as ReasoningLevel,
        prevLevels,
        supportedLevels,
      );
      setValue("model_config.reasoning_level", mapped);
    }
  }, [
    currentReasoningLevel,
    supportedLevels,
    setValue,
    immutable,
    supportsReasoning,
  ]);

  if (!supportsReasoning) {
    return null;
  }

  const options = supportedLevels.map((level) => ({
    key: level,
    value: REASONING_LEVEL_LABELS[level] ?? level,
  }));

  return (
    <FormItem
      description="Controls how much thinking the model performs internally before responding and calling tools. Increases accuracy at higher latency and cost."
      gap="sm"
      label="Reasoning level"
    >
      <Controller
        control={control}
        name="model_config.reasoning_level"
        render={({ field }) => (
          <FixedSimpleDropdown
            buttonDataLoc="agent-reasoning-level-dropdown-button"
            dataLoc="agent-reasoning-level-dropdown"
            disabled={immutable}
            elements={options}
            placeholder="Select reasoning level"
            selectedKey={field.value ?? undefined}
            onSelect={(key) => field.onChange(key as ReasoningLevel)}
          />
        )}
      />
    </FormItem>
  );
};

const NarrationLevelSelector: React.FC<{ immutable: boolean }> = ({
  immutable,
}) => {
  const { control } = useFormContext<AgentVersionConfigurationInput>();

  return (
    <FormItem
      description="Controls user-facing explanations of what the agent is doing and why. Generates additional text output alongside tool calls, in addition to internal thinking/reasoning (if any). Unlike reasoning, this does not affect accuracy significantly."
      gap={false}
      label="Narration level"
    >
      <Controller
        control={control}
        name="model_config.narration_level"
        render={({ field }) => (
          <FixedSimpleDropdown
            buttonDataLoc="agent-narration-level-dropdown-button"
            dataLoc="agent-narration-level-dropdown"
            disabled={immutable}
            elements={NARRATION_LEVEL_OPTIONS}
            placeholder="Select narration level"
            selectedKey={field.value ?? undefined}
            onSelect={(key) => field.onChange(key as NarrationLevel)}
          />
        )}
      />
    </FormItem>
  );
};

export const ExecutionSettingsEditor: React.FC<
  ExecutionSettingsEditorProps
> = ({ immutable }) => {
  const { getValues, setValue } =
    useFormContext<AgentVersionConfigurationInput>();

  const parentValues = getValues();
  const localForm = useForm<ExecutionSettingsFormValues>({
    defaultValues: {
      maxSteps: parentValues.execution_config?.max_steps_per_session ?? null,
      temperature: parentValues.model_config?.temperature ?? null,
    },
    mode: "onChange",
  });

  // Subscribe to local form changes and propagate valid changes to parent
  useEffect(
    () =>
      localForm.subscribe({
        formState: { values: true },
        callback: async ({ values: _ }) => {
          const isValid = await localForm.trigger();
          if (!isValid) return;

          // Read current values after validation to avoid using potentially
          // stale values from when the callback was triggered
          const currentValues = localForm.getValues();
          const { maxSteps, temperature } = currentValues;
          if (maxSteps !== null) {
            setValue("execution_config.max_steps_per_session", maxSteps);
          }
          if (temperature !== null) {
            setValue("model_config.temperature", temperature);
          }
        },
      }),
    [localForm, setValue],
  );

  return (
    <div className="rounded-lg bg-gray-50 px-4 py-3">
      <FormItem
        description="The total number of cycles in the agentic loop. If the agent hits the maximum number of iterations allowed, it will stop execution."
        gap="sm"
        label="Max iterations"
        isRequired
      >
        <Controller
          control={localForm.control}
          name="maxSteps"
          render={({ field, fieldState }) => (
            <>
              <Input
                disabled={immutable}
                errored={!!fieldState.error}
                max="500"
                min="1"
                type="number"
                value={field.value ?? ""}
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value === "" ? null : e.target.valueAsNumber);
                }}
              />
              {fieldState.error && (
                <ErrorHint margin="none">{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            required: "Max iterations is required",
            min: { value: 1, message: "Min value is 1" },
            max: { value: 500, message: "Max value is 500" },
          }}
        />
      </FormItem>
      <FormItem
        description="Adjust the level of randomness in the AI's output. Lower values (closer to 0) create precise and consistent responses, while higher values (closer to 1) make them more creative and varied."
        gap="sm"
        label="Temperature"
        isRequired
      >
        <Controller
          control={localForm.control}
          name="temperature"
          render={({ field, fieldState }) => (
            <>
              <Input
                disabled={immutable}
                errored={!!fieldState.error}
                max="1"
                min="0"
                step="0.1"
                type="number"
                value={field.value ?? ""}
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value === "" ? null : e.target.valueAsNumber);
                }}
              />
              {fieldState.error && (
                <ErrorHint margin="none">{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            required: "Temperature is required",
            min: { value: 0, message: "Min value is 0" },
            max: { value: 1, message: "Max value is 1" },
          }}
        />
      </FormItem>
      <ReasoningLevelSelector immutable={immutable} />
      <NarrationLevelSelector immutable={immutable} />
    </div>
  );
};
